import { useEffect } from "react";
import { useSelector } from "react-redux";
import DataTable from "../components/UI/DataTable/DataTable";
import {
  BILLING_HISTORY_TABLE_COLUMNS,
  BILLING_RULES_TABLE_COLUMNS,
  EV_RULES_TABLE_COLUMNS,
} from "../constants/Constants";
import { BILLING_PROPS, BILLING_RULE_TYPE, ROLES_HIERARCHY, TABLES } from "../enums/Enums";
import { billingActions } from "../redux/reducers/BillingReducer";
import { commonActions } from "../redux/reducers/CommonReducer";
import * as actionsService from "../services/ActionsService/ActionsService";
import * as biliingActionsService from "../services/ActionsService/BillingActionsService";
import * as validationsActions from "../services/ActionsService/ValidationsActionsService";
import store from "../redux/Store";
import { 
  getDaysAsStringFromDaysInt, 
  getBillingValidHoursString, 
  getBillingAssignedUserTypesString,
  getBillingRateString 
} from "../utils/BillingUtils";
import { showErrorMessage } from "../utils/MessagesUtils";
import { getTotalValidationAmount, getValidationHistoriesData } from "../utils/ValidationsUtils";
import { getSiteByScopeId } from "../utils/UsersUtils";
import { convertCurrencyToSymbol, convertUTCToLocalTime } from "../utils/CommonUtils";
import BillingPage from "../containers/Billing/BillingPage.module.css";
import Strings from "../resources/Strings";

const useBillingService = () => {
  const selectedScope = useSelector((state) => state.user?.selectedScope);
  const selectedSites = useSelector((state) => state.user?.selectedSites);
  const gmtUser = useSelector((state) => state.user.gmtUser);
  const canMakeActions = gmtUser?.role?.hierarchy <= ROLES_HIERARCHY.ADMINISTRATOR
  const billingRulesColumns = canMakeActions ? BILLING_RULES_TABLE_COLUMNS : BILLING_RULES_TABLE_COLUMNS.filter(c => c.field !== BILLING_PROPS.OPTIONS);
  const evRulesColumns = canMakeActions ? EV_RULES_TABLE_COLUMNS : EV_RULES_TABLE_COLUMNS.filter(c => c.field !== BILLING_PROPS.OPTIONS);
  const currentBillingRules = useSelector((state) => state.billing.billingRules);

  const sites = useSelector((state) => state.user.sites);
  const selectedSite = getSiteByScopeId(sites, selectedScope?.id);
  const siteCurrencySymbol = convertCurrencyToSymbol(selectedSite?.billingCurrency);
  const historyColumns = BILLING_HISTORY_TABLE_COLUMNS;
  const validationHistoriesData = useSelector((state) => state.billing.validationHistoriesData);

  useEffect(() => {
    if (selectedSites.length > 0 ) {
      const siteIds = selectedSites.map(site => site.id);
      if (siteIds) {
        biliingActionsService.getBillingRules(siteIds, true, false).then((response) => {
          if (response?.data?.isSuccess) {
            store.dispatch(
              billingActions.setBillingRules(response.data.entity)
            );      
          }
        });

        validationsActions.getValidationHistoriesBySiteIds(siteIds, true, false).then((response) => {
          if (response.data.isSuccess) {
            let userIds = response.data.entity.map(vh => vh.userId);
            let chargeAccountsUserIds = response.data.entity.map(vh => vh.chargeAccount.userId);

            // Concat userId and charge account
            userIds = userIds.concat(chargeAccountsUserIds);

            // Distinct array
            userIds = userIds.filter((id, index) => userIds.findIndex((userId) => userId === id) === index); 
            let scopesIds = [selectedScope?.id];
            
            actionsService.getUsersIdentityData(userIds, scopesIds, true, true).then((userIdsResponse) => {
              if (userIdsResponse.data.isSuccess) {
                const {validationHistories, validationAmountSum, validationHistoriesCount, minDate, maxDate} = getValidationHistoriesData(userIdsResponse?.data?.entity, response?.data?.entity, selectedSite, siteCurrencySymbol);
                       
                store.dispatch(billingActions.setValidationHistoriesData({
                    validationHistories,
                    validationAmountSum,
                    validationHistoriesCount,
                    minDate,
                    maxDate
                }));
              }
              else {
                //error
                showErrorMessage(userIdsResponse.data?.error);

                // reset data
                store.dispatch(billingActions.setValidationHistoriesData({
                  validationHistories: [],
                  validationAmountSum: 0,
                  validationHistoriesCount: 0,
                  minDate: null,
                  maxDate: null
                }));

                store.dispatch(commonActions.setIsShowLoadingSpinner(false));
              }
            }).catch(error => {
              showErrorMessage(error);
              store.dispatch(commonActions.setIsShowLoadingSpinner(false));
            });  

          }
          else {
            //showErrorMessage(response.data.error);
            // not founded any validation histories on sites
            // reset data
            store.dispatch(billingActions.setValidationHistoriesData({
              validationHistories: [],
              validationAmountSum: 0,
              validationHistoriesCount: 0,
              minDate: null,
              maxDate: null
            }));

            store.dispatch(commonActions.setIsShowLoadingSpinner(false));
          }
        }).catch(error => {
          showErrorMessage(error);
          store.dispatch(commonActions.setIsShowLoadingSpinner(false));
        });
      }
    }
  }, [selectedSites]);

  const getBillingRulesDataTable = (eventFn) => {
    let filteredBillingRules = currentBillingRules?.filter(
      (br) => br?.billingRuleType === BILLING_RULE_TYPE.PARKING_RULE && br?.site?.isBillingEnabled
    );

    let modifiedBillingRules = JSON.parse(JSON.stringify(filteredBillingRules)); 

    // Add data for billing rule search and display
    // When date ends with 'Z' (already on local time and convertUTCToLocalTime is not needed)
    modifiedBillingRules.forEach(br => {
      br.validDays = getDaysAsStringFromDaysInt(br);
      br.validHours = getBillingValidHoursString(br); 
      br.ruleUserTypes = getBillingAssignedUserTypesString(br);
      br.modifiedDate = br.modifiedDate.endsWith('Z') ? new Date(br?.modifiedDate)?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone }) : new Date(convertUTCToLocalTime(br?.modifiedDate))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone });
      br.classes = BillingPage
    })

    return (
      <DataTable
        onRowClick={() => {}}
        columns={billingRulesColumns}
        rows={modifiedBillingRules}
        tableId={TABLES.BILLING_RULES}
        selectedScope={selectedScope}
      ></DataTable>
    );
  };

  const getEvRulesDataTable = (eventFn) => {
    let filteredEvRules = currentBillingRules?.filter(
      (br) => br?.billingRuleType === BILLING_RULE_TYPE.EV_RULE && br?.site?.isBillingEnabled
    );

    let modifiedEvRules = JSON.parse(JSON.stringify(filteredEvRules));

    // Add data for billing rule search and display
    modifiedEvRules.forEach(er => {
      er.ruleUserTypes = getBillingAssignedUserTypesString(er);
      er.evRuleRateAmount = getBillingRateString(er);
      er.modifiedDate = er.modifiedDate.endsWith('Z') ? new Date(er?.modifiedDate)?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone }) : new Date(convertUTCToLocalTime(er?.modifiedDate))?.toLocaleString('en-US', { hour12: true, timeZone: selectedSite?.timeZone });
      er.classes = BillingPage
    });

    return (
      <DataTable
        onRowClick={() => {}}
        columns={evRulesColumns}
        rows={modifiedEvRules}
        tableId={TABLES.BILLING_RULES}
        selectedScope={selectedScope}
      ></DataTable>
    );
  };

  const getBillingValidationHistoryDataTable = (eventFn) => {
    return ( 
      <DataTable
        onRowClick={() => {}}
        columns={historyColumns}
        rows={validationHistoriesData?.validationHistories} 
        toolBarTitle={{
           "title": Strings.TOOLBAR_TITLE.VALIDATION_HISTORY, 
           "value": `${siteCurrencySymbol}${validationHistoriesData?.validationAmountSum}`
        }}
        toolBarSecondaryTitle={{
          "title": Strings.TOOLBAR_TITLE.VALIDATION_HISTORY_SECONDARY, 
          "value": `${validationHistoriesData?.validationHistoriesCount}`
        }}
        tableId={TABLES.BILLING_HISTORY} 
        selectedScope={selectedScope}
        minDate={validationHistoriesData?.minDate}
        maxDate={validationHistoriesData?.maxDate}
        onFilteredRowsChange={(event) => {
          store.dispatch(billingActions.setValidationHistoriesData({
            validationAmountSum: getTotalValidationAmount(event),
            validationHistoriesCount: event?.length,
          }));
        }}
        onSearchedRowsChange={(event) => {
          store.dispatch(billingActions.setValidationHistoriesData({
            validationAmountSum: getTotalValidationAmount(event),
            validationHistoriesCount: event?.length,
          }));
        }}
      ></DataTable>
    );
  };

  return {
    getBillingRulesDataTable,
    getEvRulesDataTable,
    getBillingValidationHistoryDataTable
  };
};

export default useBillingService;
